import React, { Component } from "react"
import JamButton from "./components/JamButton"
import KeyboardShortcuts from "./components/KeyboardShortcuts"
import SuperShortcuts from "./components/SuperShortcuts"
import isAuthenticated from "./util/isAuthenticated"
import isPermissioned from "./util/isPermissioned"
import getUserIdFromJamFilledCookie from "./util/getUserIdFromJamFilledCookie"
import getUserByIdInJamFilledStorage from "./util/fetchApiHelpers/getUserByIdInJamFilledStorage"
import getUserFromLocalStorage from "./util/getUserFromLocalStorage"
import getThreads from "./util/fetchApiHelpers/getThreads"
import JamThread from "./components/JamThread"
import JamCreateNewComment from "./components/JamCreateNewComment"
import Updates from "./components/Updates"
import getPeopleInTheJam from "./util/getPeopleInTheJam"
import clickIsNotOnJamElement from "./util/fetchApiHelpers/clickIsNotOnJamElement"
import DoubleClickToCommentInstructionBanner from "./components/DoubleClickToCommentInstructionBanner"
import getCookieFromQueryString from "./util/getCookieFromQueryString"
import io from "socket.io-client"
import * as SocketIOClient from "socket.io-client"
import Toolbar from "./components/Toolbar"
import getJamFilledCookie from "./util/getJamFilledCookie"
// pull in config vars
import * as config from "./util/config"
import ToastContainer from "./components/ToastContainer"
import LoginModal from "./components/LoginModal"
import getUserIdFromLocalStorage from "./util/getUserIdFromLocalStorage"
import { select } from "optimal-select"
import InviteModal from "./components/InviteModal"
import MutationObserver from "react-mutation-observer"
import validateToken from "./util/fetchApiHelpers/validateToken"
import jamIsPublic from "./util/fetchApiHelpers/jamIsPublic"
import Loading from "./components/Loading"
import UserSettingsModal from "./components/UserSettingsModal"
import SaysHi from "./components/SaysHi"
import getTextFromComment from "./util/getTextFromComment"
import getTypeOfNotification from "./util/getTypeOfNotification"
import parseNotificationUrl from "./util/parseNotificationUrl"
import getIndexOfThreads from "./util/getIndexOfThreads"
import handleQueryStringForDirectLinksToComments from "./util/handleQueryStringForDirectLinksToComments"
import type { User, Comment, Thread, Mention, Notification } from "./types"
import { ConfettiProvider } from "./components/Confetti"
import { UserContext } from "./components/UserContext"
import { updateLocalUser } from "./util/updateLocalUser"

interface JamProps {
	JAM_ID: string
}

interface JamState {
	ready: boolean
	jamFilledCookie: string
	clickX: number | null
	clickY: number | null
	jamTime: boolean
	jamIsPublic: boolean
	newCommentEditorOpen: boolean
	threads: Thread[]
	mentions: Mention[]
	whichThreadIsOpen: number // thread index with open preview since only one can be open at a time. -1 is all closed
	hasPermission: boolean // check for permission for user to jam on this Jam
	connectedUsers: User[]
	elementSelectorForNewThread: "body"
	showUpdates: boolean
	loading: boolean
	displayLoginModal: boolean
	specificElement: string | Element // TODO: ????
	showInviteModal: boolean
	jamLoading: boolean
	notifications: Notification[]
	loggedInUser?: User | false
	copySuggestingModeElement?: HTMLElement
	copySuggestMode?: boolean
	showUserSettingsModal?: boolean
	currentHref?: string
	whoSaysHi?: string | false
	saysHiTo?: string | false
	detachedThreadToDisplay?: any
	typeOfDetachedThread?: any
	tempThreadIndexForUpdateThreadFunction?: number
}

export default class Jam extends Component<JamProps, JamState> {
	state: JamState = {
		ready: false,
		jamFilledCookie: "",
		clickX: null,
		clickY: null,
		jamTime: false,
		jamIsPublic: true,
		newCommentEditorOpen: false,
		threads: [],
		mentions: [],
		whichThreadIsOpen: -1, // thread index with open preview since only one can be open at a time. -1 is all closed
		hasPermission: false, // check for permission for user to jam on this Jam
		connectedUsers: [],
		elementSelectorForNewThread: "body",
		showUpdates: false,
		loading: false,
		displayLoginModal: false,
		specificElement: "",
		showInviteModal: false,
		jamLoading: false,
		notifications: [],
	}

	socket: SocketIOClient.Socket | undefined

	componentDidMount = async () => {
		// const { jamTime } = this.state

		this.handleQueryString()

		setInterval(() => {
			this.checkForUrlChange()
		}, 1000)

		// if cookie is in the query param, save it as a cookie and in local storage
		const queryStringContainsCookie = window.location.search.includes(
			"jam_filled_cookie",
		)
		if (queryStringContainsCookie) {
			const cookie = getCookieFromQueryString(window.location.search)
			document.cookie = `jam_filled_cookie=${cookie}, domain=${window.location.host}`
			await getUserByIdInJamFilledStorage(this.props.JAM_ID)
			window.location.search = ""
		}

		// on mobile 3 finger tap to jam - one finger for each letter of jam
		document.addEventListener("touchstart", (e) => {
			if (e.targetTouches.length == 3) {
				this.toggleJamMode()
			}
		})

		// if authenticated, turn on jamMode
		// off for the demo site to keep clutter off the screen for first impression
		if (
			(await isAuthenticated(this.props.JAM_ID)) &&
			window.location.host !== "jam.dev"
		) {
			this.toggleJamMode()
		} else {
			// if (jamTime) {
			//   await this.fetchInitialJamTimeData();
			// }
		}
	}

	componentDidUpdate = async (prevProps: JamProps, prevState: JamState) => {
		if (this.state.loggedInUser && !prevState.loggedInUser) {
			if (this.state.jamTime) {
				this.setState({ displayLoginModal: false })
				await this.initSockets()
				await this.fetchInitialJamTimeData()
			}
		}
	}

	fetchInitialJamTimeData = async () => {
		// check users permission and auth
		await this.handleAuthentication()

		// fetch initial threads
		this.setState(
			{
				threads: (await getThreads(this.props.JAM_ID)) as Thread[],
				jamIsPublic: await jamIsPublic(this.props.JAM_ID),
			},
			async () => {
				this.setState({ ready: true })
			},
		)

		// get the people in the jam for mentions in the editor
		this.setState({
			mentions: (await getPeopleInTheJam(this.props.JAM_ID)) as Mention[],
		})
	}

	initSockets = () => {
		// init sockets
		this.socket = io(
			`${config.SOCKET_SERVER}?jamId=${
				this.props.JAM_ID
			}&${getJamFilledCookie()}&jam-filled-userid=${getUserIdFromLocalStorage()}&jamClientHost=${
				window.location.origin
			}`,
		)

		this.socket.emit("jamTimeSwitchedOn")
		this.socket.on("connectedUsersUpdated", (connectedUsers: User[]) => {
			this.updateConnectedUsers(connectedUsers)
		})

		this.socket.on("threadUpdated", (thread: Thread) => {
			this.updateThread(thread, false)

			// also push notification

			if (
				this.state.loggedInUser &&
				thread.comments[thread.comments.length - 1].author.name !==
					this.state.loggedInUser.name
			) {
				this.notify(
					thread,
					thread.comments[thread.comments.length - 1],
					thread.location.originalUrl,
				)
			}
		})

		this.socket.on("threadResolved", (threadId: string) => {
			this.deleteThread(threadId)
		})

		this.socket.on("newCommentReaction", (newComment: Comment) => {
			this.updateComment(newComment)
		})

		this.socket.on("removeOldUserFromMentions", (name: string) => {
			this.removeOldUserFromMentionsByName(name)
		})

		this.socket.on("sayhello", (toFromObj: { to: string; from: string }) => {
			console.log(
				"got a sayhello",
				toFromObj.from,
				toFromObj.to,
				this.state.loggedInUser && this.state.loggedInUser.name,
			)
			if (
				this.state.loggedInUser &&
				(toFromObj.to === this.state.loggedInUser.name ||
					toFromObj.from === this.state.loggedInUser.name)
			) {
				this.someoneSaysHi(toFromObj.from, toFromObj.to)
			}
		})
	}

	handleClick = (e: any) => {
		const {
			whichThreadIsOpen,
			newCommentEditorOpen,
			copySuggestingModeElement,
		} = this.state

		// if was editing copy, let's undo
		if (
			copySuggestingModeElement &&
			copySuggestingModeElement.contentEditable &&
			e.target !== copySuggestingModeElement
		) {
			copySuggestingModeElement.contentEditable = "false"
		}

		if (clickIsNotOnJamElement(e)) {
			// if editor is open, close it
			if (newCommentEditorOpen) {
				this.setState({ newCommentEditorOpen: false })
			} else if (whichThreadIsOpen > -1) {
				// else if a thread is open, close it
				this.setState({ whichThreadIsOpen: -1 })
			}

			// if click is on a p or h1-6 tag, let's edit it
			// this feature is so janky, commenting it out
			// if (
			// 	e.target.nodeName === "P" ||
			// 	(e.target.nodeName.length === 2 && e.target.nodeName.charAt(0) === "H")
			// ) {
			// 	this.setState({ copySuggestingModeOriginalText: e.target.innerText })
			// 	this.setState({ copySuggestingModeElement: e.target })

			// 	e.target.contentEditable = "true"
			// 	e.target.addEventListener("keypress", this.handleSuggestingCopy)
			// }

			// if shift is held down during click, let's celebrate good times come on
			// if (e.shiftKey) this.createNewVideoReaction(e)
		}
	}

	handleDoubleClick = (e: MouseEvent) => {
		const { whichThreadIsOpen, newCommentEditorOpen, showUpdates } = this.state
		if (clickIsNotOnJamElement(e)) {
			// if editor is open, close it
			if (newCommentEditorOpen) {
				this.setState({ newCommentEditorOpen: false })
			} else if (whichThreadIsOpen > -1) {
				// else if a thread is open, close it
				this.setState({ whichThreadIsOpen: -1 })
			} else {
				// else create a new commment
				if (!showUpdates) {
					this.createNewComment(e)
				}
			}
		}
	}

	toggleJamMode = async () => {
		const newJamMode = !this.state.jamTime

		this.setState({ jamTime: newJamMode })
		//  if jam time, make sure user is authed and has permissions
		if (newJamMode) await this.handleAuthentication()

		// if first jam mode, go fetch all the things
		if (newJamMode && this.state.loggedInUser && !this.state.threads.length)
			await this.fetchInitialJamTimeData()

		// click event handler
		if (newJamMode) {
			if (this.socket) this.socket.emit("jamTimeSwitchedOn")
			document.addEventListener("click", this.handleClick)
			document.addEventListener("dblclick", this.handleDoubleClick)
		} else {
			document.removeEventListener("click", this.handleClick)
			document.removeEventListener("dblclick", this.handleDoubleClick)
			if (this.socket) this.socket.emit("jamTimeSwitchedOff")
		}
	}

	toggleUpdates = async () => {
		this.setState((state) => {
			return { showUpdates: !state.showUpdates }
		})
	}

	onCloseUpdates = async () => {
		this.setState({ showUpdates: false })
	}

	hideLoginModal = () => {
		this.setState({ displayLoginModal: false, jamTime: false })
	}

	handleAuthentication = async () => {
		const authenticated = await isAuthenticated(this.props.JAM_ID) // check if jam_filled_cookie present
		// check that auth token is valid
		let validCookie = false
		if (
			authenticated &&
			document.cookie
				.split(";")
				.some((cookie) => cookie.trim().startsWith("jam_filled_cookie="))
		) {
			validCookie = await validateToken()
			console.log(`cookie valid? ${validCookie}`)
		}

		if (authenticated) {
			if (!validCookie && !this.state.jamIsPublic) {
				// go and get authenticated!
				this.setState({ displayLoginModal: true })
			}
			// if the user is in local storage, get user and set them to local storage
			if (!getUserFromLocalStorage()) {
				await getUserByIdInJamFilledStorage(this.props.JAM_ID)
			}

			// then get user from local storage
			this.setState({ loggedInUser: getUserFromLocalStorage() })

			// check permissions - can they access this jam?
			const permissioned = await isPermissioned(this.props.JAM_ID)
			this.setState({ hasPermission: permissioned })

			// if no, need to redirect them to request permission
			if (!permissioned)
				window.open(
					`${config.API_HOSTNAME}/request_to_jam?jamId=${
						this.props.JAM_ID
					}&userId=${getUserIdFromJamFilledCookie()}`,
					"_blank",
				)
		} else {
			// go and get authenticated!
			this.setState({ displayLoginModal: true })
		}
	}

	setLoggedInUserFromLocalStorage = () => {
		this.setState({ loggedInUser: getUserFromLocalStorage() })
	}

	createNewComment = (e: any) => {
		const element =
			!e && this.state.copySuggestMode
				? this.state.copySuggestingModeElement
				: e.target
		// const styles = getCssRules(element);

		// if no copy suggest mode but element == this.state.copySuggestingModeElement, remove contenteditable so that wont be the selector
		if (
			!this.state.copySuggestMode &&
			this.state.copySuggestingModeElement === e.target
		) {
			console.log(
				"if no copy suggest mode but element == this.state.copySuggestingModeElement, remove contenteditable so that wont be the selector",
			)
			element.contentEditable = "false"
		}

		const selector = select(element, {
			ignore: {
				attribute(name: string) {
					return /contenteditable|style|spellcheck|clearfix/.test(name)
				},
			},
		})

		console.log(
			`in create new comment, !e is ${!e} and this.state.copySuggestMode is ${
				this.state.copySuggestMode
			}`,
		)

		this.setState({
			newCommentEditorOpen: true,
			clickX:
				!e && this.state.copySuggestMode
					? element.getBoundingClientRect().x + window.scrollX
					: e.pageX,
			clickY:
				!e && this.state.copySuggestMode
					? element.getBoundingClientRect().y + window.scrollY
					: e.pageY,
			elementSelectorForNewThread: selector,
			specificElement: element,
		})
	}

	updateThread = (newThread: Thread, keepThreadOpen = true) => {
		// this is called after a comment is posted, when thread is moved, and when thread is resolved, to update the thread in the ui
		const { threads } = this.state
		const tempThreads = threads
		const index = threads.findIndex((thread) => thread._id === newThread._id)
		index > -1 ? (tempThreads[index] = newThread) : tempThreads.push(newThread)
		this.setState(
			{ threads: tempThreads, tempThreadIndexForUpdateThreadFunction: index },
			() => {
				// to keep the thread open after commenting
				if (keepThreadOpen) {
					const { tempThreadIndexForUpdateThreadFunction, threads } = this.state
					if (tempThreadIndexForUpdateThreadFunction === -1) {
						this.updateWhichThreadIsOpen(threads.length - 1)
					} else if (
						typeof tempThreadIndexForUpdateThreadFunction !== "undefined"
					) {
						this.updateWhichThreadIsOpen(tempThreadIndexForUpdateThreadFunction)
					}
				}
			},
		)

		if (keepThreadOpen) {
			// if newComment open, close it
			this.setState({ newCommentEditorOpen: false })
		}
	}

	updateComment = (newComment: Comment) => {
		// called after someone reacts to update the comment
		const { threads } = this.state
		const threadIndex = threads.findIndex(
			(thread) => thread._id === newComment.threadId,
		)
		if (threadIndex > -1) {
			const threadComments = threads[threadIndex].comments
			const commentIndex = threadComments.findIndex(
				(comment) => comment._id === newComment._id,
			)
			const tempThread = threads[threadIndex]
			tempThread.comments[commentIndex] = newComment
			this.updateThread(tempThread)
		}
	}

	// used for resolving threads
	deleteThread = (threadId: string) => {
		try {
			const { threads } = this.state
			const tempThreads = threads
			const index = threads.findIndex((thread) => thread._id === threadId)
			tempThreads.splice(index, 1)
			this.setState({ threads: tempThreads })
			this.updateWhichThreadIsOpen(-1)
		} catch (e) {
			console.log(e)
		}
	}

	updateWhichThreadIsOpen = (threadId: number) => {
		this.setState({ whichThreadIsOpen: threadId })
	}

	updateConnectedUsers = (connectedUsers: User[]) => {
		this.setState({ connectedUsers })

		// if there is a connected user who was not fetched as part of mentions, add them to the mentions list
		for (let i = 0; i < connectedUsers.length; i++) {
			if (
				!this.state.mentions.find(
					(user) => user.name === connectedUsers[i].name,
				)
			) {
				let mentions = this.state.mentions
				mentions.push()
				this.setState({
					mentions: [
						...this.state.mentions,
						{
							name: connectedUsers[i].name,
							avatar: connectedUsers[i].avatar,
						},
					],
				})
			}
		}
	}

	removeOldUserFromMentionsByName = (name: string) => {
		let tempMentions = this.state.mentions
		tempMentions = tempMentions.filter((user) => user.name !== name)
		this.setState({ mentions: tempMentions })
	}

	updateLoadingStatus = (loading: boolean) => {
		this.setState({ loading: loading })
	}

	// handleSuggestingCopy = async (e: any) => {
	// 	// this feature is so janky, commenting it out
	// 	// if (e.key === "Enter") {
	// 	// 	e.preventDefault()
	// 	// 	e.target.contentEditable = "false"
	// 	// 	// create the new thread (note to self this should one day be broken out into something reusable)
	// 	// 	// get system info
	// 	// 	const deviceDetector = new DeviceDetector()
	// 	// 	const userAgent = navigator.userAgent
	// 	// 	const device = deviceDetector.parse(userAgent)
	// 	// 	const screenWidth = window.innerWidth
	// 	// 	const screenHeight = window.innerHeight
	// 	// 	const selector = select(e.target, {
	// 	// 		ignore: {
	// 	// 			attribute(name) {
	// 	// 				return /contenteditable|style|spellcheck|clearfix/.test(name)
	// 	// 			},
	// 	// 		},
	// 	// 	})
	// 	// 	// if the text has changed, create a new thread
	// 	// 	if (this.state.copySuggestingModeOriginalText !== e.target.innerText) {
	// 	// 		const newThread = await createNewThread(
	// 	// 			e.target.getBoundingClientRect().x + window.scrollX, // x
	// 	// 			e.target.getBoundingClientRect().y + window.scrollY - 70, // y (-70 to show preview above the text)
	// 	// 			`{"blocks":[{"key":"1fm1q","text":"","type":"unstyled","depth":0,"inlineStyleRanges":[],"entityRanges":[],"data":{}}],"entityMap":{}}`, // message text
	// 	// 			this.props.JAM_ID, // jamid
	// 	// 			false, // screenshot
	// 	// 			device,
	// 	// 			screenWidth,
	// 	// 			screenHeight,
	// 	// 			selector, // element selector
	// 	// 			"", // css input
	// 	// 			this.state.copySuggestingModeOriginalText, // original text
	// 	// 			e.target.innerText, // new text
	// 	// 		)
	// 	// 		await this.updateThread(newThread)
	// 	// 		this.turnOffCopySuggestMode()
	// 	// 		this.socket.emit("threadUpdate", newThread)
	// 	// 	}
	// 	// }
	// }

	turnOffCopySuggestMode = () => {
		this.setState({ copySuggestMode: false })
	}

	toggleInviteModal = () => {
		this.setState({ showInviteModal: !this.state.showInviteModal })
	}

	isAJamElement = async (elem: HTMLElement) => {
		const classString = elem.classList.toString()
		const parentElement = await elem.parentElement

		if (classString.includes("jam--") || classString.includes("Draft")) {
			return true
		}
		if (parentElement && parentElement.classList.toString().includes("Draft")) {
			return true
		}

		return false
	}

	onDomChange = (e: any) => {
		const { ready, jamTime } = this.state
		const elem = e.target
		const emptyDiv = e.target.innerHTML === ""

		if (ready && jamTime && elem.classList && !emptyDiv) {
			if (!this.isAJamElement(elem)) {
				this.rerenderThreads()
			}
		}
	}

	handleResize = () => {
		this.rerenderThreads()
	}

	rerenderThreads = async () => {
		this.setState({
			threads: this.state.threads,
		})
	}

	showUserSettingsModal = () => {
		this.setState({ showUserSettingsModal: true })
	}

	hideUserSettingsModal = () => {
		this.setState({ showUserSettingsModal: !this.state.showUserSettingsModal })
	}

	someoneSaysHi = (fromName: string, toName: string) => {
		this.setState({ whoSaysHi: fromName, saysHiTo: toName }, () => {
			setTimeout(() => {
				this.setState({ whoSaysHi: false, saysHiTo: false })
			}, 5000)
		})
	}

	checkForUrlChange = () => {
		if (this.state.jamTime && typeof window !== "undefined") {
			if (this.state.currentHref !== window.location.href) {
				this.setState({ currentHref: window.location.href })
				this.rerenderThreads()
			}
		}
	}

	notify = (thread: Thread, comment: Comment, url: string) => {
		// TODO choose whether to show notitification based on same host and brand new thread vs reply to someone else
		const notification: Notification = {
			avatar: comment.author.avatar,
			author: comment.author.name,
			type: getTypeOfNotification(
				thread,
				comment,
				this.state.loggedInUser ? this.state.loggedInUser.name : "",
			),
			text: getTextFromComment(comment),
			url: parseNotificationUrl(url),
			threadY: thread.y,
			threadIndex: getIndexOfThreads(thread._id, this.state.threads),
			id: Math.ceil(Math.random() * 1000000),
		} as any
		this.setState(
			{ notifications: this.state.notifications.concat(notification) },
			() => {
				console.log(this.state.notifications)
				setTimeout(() => {
					const newNotificationsArray = this.state.notifications.filter(
						(n: any) => n.id !== notification.id,
					)
					console.log(newNotificationsArray)
					this.setState({ notifications: newNotificationsArray })
				}, 4000)
			},
		)
	}

	handleQueryString = async () => {
		const {
			enableJam,
			detachedThreadToDisplay,
			typeOfDetachedThread,
			indexOfThreadIfOnThisPage,
			// elementThreadIsAttachedTo,
		} = await handleQueryStringForDirectLinksToComments(
			this.props.JAM_ID,
			this.state.threads,
		)
		if (enableJam && !this.state.jamTime) this.toggleJamMode()
		if (detachedThreadToDisplay)
			this.setState({ detachedThreadToDisplay, typeOfDetachedThread })
		if (indexOfThreadIfOnThisPage) {
			this.updateWhichThreadIsOpen(indexOfThreadIfOnThisPage)
			// goToThread(
			// 	elelementThreadIsAttachedTo,
			// 	detachedThreadToDisplay.location.percentFromTop,
			// )
		}
	}

	render() {
		const socket = this.socket
		const {
			ready,
			hasPermission,
			jamTime,
			loggedInUser,
			threads,
			whichThreadIsOpen,
			newCommentEditorOpen,
			clickX,
			clickY,
			mentions,
			elementSelectorForNewThread,
			showUpdates,
			loading,
			// styles,
			notifications,
			whoSaysHi,
			saysHiTo,
		} = this.state
		window.addEventListener("resize", this.handleResize)

		return (
			<UserContext.Provider
				value={{
					user: loggedInUser,
					updateUser: (patch) => {
						if (!loggedInUser) {
							return
						}

						updateLocalUser(patch)
						this.setState({ loggedInUser: { ...loggedInUser, ...patch } })
					},
				}}
			>
				<ConfettiProvider>
					<div>
						{!ready &&
							jamTime &&
							window.location.hostname !== "jam.dev" &&
							window.location.hostname !== "app.jam.dev" && <Loading />}

						{ready && jamTime && hasPermission && (
							<JamButton toggleJamMode={this.toggleJamMode} jamTime={jamTime} />
						)}

						{/* load the little double click to leave a comment instruction */}
						{ready && jamTime && hasPermission && (
							<DoubleClickToCommentInstructionBanner />
						)}

						{ready &&
							jamTime &&
							hasPermission &&
							loggedInUser &&
							showUpdates && (
								<Updates
									threads={threads}
									user={loggedInUser}
									onClose={this.onCloseUpdates}
									onOpenThread={this.updateWhichThreadIsOpen}
								/>
							)}

						{<KeyboardShortcuts onToggleJamMode={this.toggleJamMode} />}

						{/* pull in super shortcuts */}
						{ready && jamTime && hasPermission && (
							<SuperShortcuts
								threads={threads}
								currentOpenThread={this.state.whichThreadIsOpen}
								onToggleUpdates={this.toggleUpdates}
								onChangeOpenThread={this.updateWhichThreadIsOpen}
								onMessageSend={() => {
									throw new Error("TODO")
								}}
							/>
						)}
						{/* if newCommentEditorOpen, add the new comment editor at state x and y */}
						{/* for below into JamCreateNewComment: styles={styles}*/}
						{ready &&
							jamTime &&
							hasPermission &&
							newCommentEditorOpen &&
							socket &&
							clickX &&
							clickY &&
							loggedInUser && (
								<JamCreateNewComment
									x={clickX}
									y={clickY}
									elementSelectorForNewThread={elementSelectorForNewThread}
									specificElement={this.state.specificElement as any}
									JAM_ID={this.props.JAM_ID}
									mentions={mentions}
									onThreadChange={this.updateThread}
									loading={loading}
									onLoadingChange={this.updateLoadingStatus}
									socket={socket}
								/>
							)}
						{/* login modal if need to login */}
						{jamTime && this.state.displayLoginModal && (
							<LoginModal
								JAM_ID={this.props.JAM_ID}
								onAuthenticated={this.handleAuthentication}
								onClose={this.hideLoginModal}
							/>
						)}

						{/* invite modal */}
						{jamTime && this.state.showInviteModal && (
							<InviteModal
								onClose={this.toggleInviteModal}
								JAM_ID={this.props.JAM_ID}
							/>
						)}

						{/* user settings modal */}
						{jamTime &&
							this.state.showUserSettingsModal &&
							loggedInUser &&
							this.socket && (
								<UserSettingsModal
									onClose={this.hideUserSettingsModal}
									user={loggedInUser}
									onUserHasUpdated={this.setLoggedInUserFromLocalStorage}
									socket={this.socket}
								/>
							)}

						{/* notifications */}
						{ready && jamTime && hasPermission && notifications.length > 0 && (
							<ToastContainer
								notifications={notifications}
								updateWhichThreadIsOpen={this.updateWhichThreadIsOpen}
							/>
						)}

						{/* someone said hi */}
						{jamTime && ready && whoSaysHi && saysHiTo && loggedInUser && (
							<SaysHi
								from={whoSaysHi}
								to={saysHiTo}
								currentUsername={loggedInUser.name}
							/>
						)}

						{/* show connected users */}
						{ready && jamTime && hasPermission && socket && (
							<Toolbar
								connectedUsers={this.state.connectedUsers}
								showUserSettingsModal={this.showUserSettingsModal}
								sockets={socket}
							/>
						)}

						{ready && jamTime && (
							<MutationObserver
								onContentChange={this.onDomChange}
								onChildRemoval={this.onDomChange}
								onChildAddition={this.onDomChange}
							>
								<div data-html2canvas-ignore="true">
									{/* load the jam button */}

									{/* when ready, and in jam mode, load threads */}
									{ready &&
										jamTime &&
										hasPermission &&
										threads.length &&
										socket &&
										loggedInUser &&
										threads.map((thread, index) => (
											<JamThread
												key={thread._id}
												JAM_ID={this.props.JAM_ID}
												thread={thread}
												detachedThread={null as any}
												mentions={mentions}
												socket={socket}
												typeOfDetachedThread={null as any}
												threadOpen={whichThreadIsOpen === index}
												threadIndex={index}
												onSelectThread={this.updateWhichThreadIsOpen}
												isShowingNotifications={showUpdates}
												onThreadChange={this.updateThread}
												onThreadResolve={this.deleteThread}
												onCommentChange={this.updateComment}
												loading={loading}
												onLoadingChange={this.updateLoadingStatus}
											/>
										))}
								</div>
							</MutationObserver>
						)}
					</div>
				</ConfettiProvider>
			</UserContext.Provider>
		)
	}
}
